import React, { createContext, useContext, ReactNode } from 'react'
import { useAuth } from '../hooks/useAuth'

/**
 * Authentication user info
 */
export interface AuthUser {
  id: string
  email?: string
  displayName?: string
  roles?: string[]
  metadata?: Record<string, any>
}

/**
 * Context value interface
 */
interface AuthContextValue {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  userRoles: string[]
  login: () => void
  logout: () => void
  validateToken: () => Promise<boolean>
  refreshToken: () => Promise<boolean>
  clearError: () => void
}

/**
 * Context creation
 */
const AuthContext = createContext<AuthContextValue | null>(null)

/**
 * Provider props
 */
interface AuthProviderProps {
  children: ReactNode
  apiBaseUrl?: string
}

/**
 * AuthProvider component
 * Provides authentication state for SSO-based access via Cognito + Microsoft Entra ID.
 */
export function AuthProvider({ children, apiBaseUrl }: AuthProviderProps): JSX.Element {
  const authData = useAuth(apiBaseUrl)

  return (
    <AuthContext.Provider value={authData}>
      {children}
    </AuthContext.Provider>
  )
}

/**
 * Hook to consume auth context.
 * Must be used within an AuthProvider.
 */
export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider')
  }

  return context
}

/**
 * Export context for advanced use cases
 */
export { AuthContext }
